/**
 * Carousel sidebar anchor navigation
 *
 * Pairs `.anchor-navigation-link[data-carousel-panel]` with matching
 * `.carousel-sidebar-info[data-carousel-panel]` and
 * `.carousel-dynamic[data-carousel-panel]` inside `.carousel-sidebar-anchor`.
 * Requires matching data-carousel-panel values in the block editor.
 *
 * @since 1.0.0
 */
import { syncCarouselControls } from './carousel.js';
import { initSingleDynamicCarousel } from './carousel-dynamic.js';

const hiddenClass = 'is-carousel-panel-hidden';
const carouselHeightVar = '--sidebar-carousel-panel-min-height';
const infoHeightVar = '--sidebar-carousel-info-min-height';
const resizeDebounceMs = 150;
const mobileDropdownClass = 'carousel-sidebar-anchor--mobile-dropdown';
const mobileMediaQuery = '(max-width: 991px)';

/**
 * @returns {boolean}
 */
function isMobileViewport() {
	return window.matchMedia(mobileMediaQuery).matches;
}

/**
 * @param {Element} panel
 * @param {() => number} measure
 * @returns {number}
 */
function measureWhileVisible(panel, measure) {
	const hadHiddenAttr = panel.hasAttribute('hidden');
	const hadHiddenClass = panel.classList.contains(hiddenClass);

	const previousInlinePosition = panel.style.position;
	const previousInlineVisibility = panel.style.visibility;
	const previousInlinePointerEvents = panel.style.pointerEvents;
	const previousInlineInset = panel.style.inset;
	const previousInlineWidth = panel.style.width;

	// If it's already visible, just measure.
	if (!hadHiddenAttr && !hadHiddenClass) {
		return measure();
	}

	try {
		panel.removeAttribute('hidden');
		panel.classList.remove(hiddenClass);

		// Take it out of flow to avoid page jump while measuring.
		panel.style.position = 'absolute';
		panel.style.inset = '0 auto auto 0';
		panel.style.width = '100%';
		panel.style.visibility = 'hidden';
		panel.style.pointerEvents = 'none';

		return measure();
	} finally {
		if (hadHiddenAttr) panel.setAttribute('hidden', '');
		if (hadHiddenClass) panel.classList.add(hiddenClass);

		panel.style.position = previousInlinePosition;
		panel.style.visibility = previousInlineVisibility;
		panel.style.pointerEvents = previousInlinePointerEvents;
		panel.style.inset = previousInlineInset;
		panel.style.width = previousInlineWidth;
	}
}

/**
 * @param {Element} panel
 * @param {boolean} visible
 */
function setPanelVisible(panel, visible) {
	if (visible) {
		panel.removeAttribute('hidden');
		panel.classList.remove(hiddenClass);
		panel.setAttribute('aria-hidden', 'false');
	} else {
		panel.setAttribute('hidden', '');
		panel.classList.add(hiddenClass);
		panel.setAttribute('aria-hidden', 'true');
	}
}

/**
 * @param {Element} anchor
 * @param {boolean} active
 */
function setAnchorActive(anchor, active) {
	anchor.classList.toggle('is-active', active);

	if (active) {
		anchor.setAttribute('aria-current', 'true');
	} else {
		anchor.removeAttribute('aria-current');
	}
}

/**
 * @param {Element} anchor
 * @returns {{label: string, icon: HTMLImageElement | null}}
 */
function getAnchorDisplayData(anchor) {
	const labelElement = anchor.querySelector('p, .cards-filter-card-label, .anchor-navigation-link-label');
	const label = (labelElement?.textContent || anchor.textContent || '').trim();
	const image = anchor.querySelector('img');

	return {
		label,
		icon: image ? image.cloneNode(true) : null,
	};
}

/**
 * Re-sync pagination inside .splide__arrows after Splide refresh / layout changes.
 *
 * @param {Element} panel
 */
function scheduleCarouselControlSync(panel) {
	window.requestAnimationFrame(() => {
		syncCarouselControls(panel);
	});
}

/**
 * @param {Element} panel
 */
function refreshCarousel(panel) {
	const splide = panel.splide;

	if (splide) {
		splide.go(0);
		splide.refresh();
		scheduleCarouselControlSync(panel);
	}
}

/**
 * @param {Element} section
 */
function initCarouselSidebarAnchorSection(section) {
	const anchors = [
		...section.querySelectorAll('.anchor-navigation-links [data-carousel-panel]'),
	];
	const carouselPanels = [...section.querySelectorAll('.carousel-dynamic[data-carousel-panel]')];
	const infoPanels = [...section.querySelectorAll('.carousel-sidebar-info[data-carousel-panel]')];
	const isMobileDropdownVariant = section.classList.contains(mobileDropdownClass);
	const linksContainer = section.querySelector('.anchor-navigation-links');
	let dropdownTrigger = null;
	let dropdownIcon = null;
	let dropdownLabel = null;
	let dropdownOpen = false;

	function closeDropdown() {
		if (!isMobileDropdownVariant || !dropdownTrigger || !linksContainer) {
			return;
		}

		dropdownOpen = false;
		dropdownTrigger.setAttribute('aria-expanded', 'false');
		linksContainer.classList.remove('is-open');
		dropdownTrigger.focus({ preventScroll: true });
	}

	function toggleDropdown() {
		if (!isMobileDropdownVariant || !dropdownTrigger || !linksContainer || !isMobileViewport()) {
			return;
		}

		dropdownOpen = !dropdownOpen;
		dropdownTrigger.setAttribute('aria-expanded', dropdownOpen ? 'true' : 'false');
		linksContainer.classList.toggle('is-open', dropdownOpen);
	}

	function syncDropdownTrigger(anchor) {
		if (!isMobileDropdownVariant || !dropdownIcon || !dropdownLabel) {
			return;
		}

		const { label, icon } = getAnchorDisplayData(anchor);
		dropdownLabel.textContent = label;
		dropdownIcon.innerHTML = '';

		if (icon) {
			icon.classList.add('anchor-navigation-dropdown-trigger-image');
			dropdownIcon.appendChild(icon);
		}
	}

	if (isMobileDropdownVariant && linksContainer) {
		let dropdownWrapper = linksContainer.closest('.anchor-navigation-dropdown');

		if (!dropdownWrapper) {
			dropdownWrapper = document.createElement('div');
			dropdownWrapper.className = 'anchor-navigation-dropdown';
			linksContainer.parentNode?.insertBefore(dropdownWrapper, linksContainer);
			dropdownWrapper.appendChild(linksContainer);
		}

		const triggerMarkup = document.createElement('button');
		triggerMarkup.type = 'button';
		triggerMarkup.className = 'anchor-navigation-dropdown-trigger';
		triggerMarkup.setAttribute('aria-expanded', 'false');
		triggerMarkup.setAttribute('aria-haspopup', 'listbox');
		triggerMarkup.innerHTML =
			'<span class="anchor-navigation-dropdown-trigger-icon" aria-hidden="true"></span>' +
			'<span class="anchor-navigation-dropdown-trigger-label"></span>' +
			'<svg class="anchor-navigation-dropdown-trigger-chevron" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 16 16"><path fill="#665e5c" d="m13.53 6.53-5 5a.75.75 0 0 1-1.062 0l-5-5a.751.751 0 1 1 1.063-1.062L8 9.938l4.47-4.47a.751.751 0 0 1 1.062 1.062z"/></svg>';

		if (!dropdownWrapper.querySelector('.anchor-navigation-dropdown-trigger')) {
			dropdownWrapper.insertBefore(triggerMarkup, linksContainer);
		}

		dropdownTrigger =
			dropdownWrapper.querySelector('.anchor-navigation-dropdown-trigger') || triggerMarkup;
		dropdownIcon = dropdownTrigger.querySelector('.anchor-navigation-dropdown-trigger-icon');
		dropdownLabel = dropdownTrigger.querySelector('.anchor-navigation-dropdown-trigger-label');

		if (!dropdownTrigger.hasAttribute('data-initialized')) {
			dropdownTrigger.setAttribute('data-initialized', 'true');

			dropdownTrigger.addEventListener('click', (event) => {
				event.preventDefault();
				toggleDropdown();
			});

			dropdownTrigger.addEventListener('keydown', (event) => {
				if (event.key === 'Enter' || event.key === ' ') {
					event.preventDefault();
					toggleDropdown();
				}
			});

			document.addEventListener('click', (event) => {
				if (!dropdownWrapper.contains(event.target)) {
					closeDropdown();
				}
			});

			document.addEventListener('keydown', (event) => {
				if (event.key === 'Escape') {
					closeDropdown();
				}
			});
		}
	}

	if (anchors.length === 0 || carouselPanels.length === 0) {
		return;
	}

	/** @type {Map<string, Element>} */
	const carouselPanelMap = new Map();

	let resizeTimeout = null;

	const liveRegion = (() => {
		const region = document.createElement('div');
		region.setAttribute('role', 'status');
		region.setAttribute('aria-live', 'polite');
		region.style.position = 'absolute';
		region.style.left = '-9999px';
		region.style.width = '1px';
		region.style.height = '1px';
		region.style.overflow = 'hidden';
		section.appendChild(region);
		return region;
	})();

	carouselPanels.forEach((panel) => {
		const panelId = panel.getAttribute('data-carousel-panel');

		if (panelId) {
			carouselPanelMap.set(panelId, panel);
		}
	});

	function clearLockedPanelHeight() {
		section.style.removeProperty(carouselHeightVar);
		section.style.removeProperty(infoHeightVar);
	}

	/**
	 * @param {Element} carouselPanel
	 * @returns {number}
	 */
	function measureCarouselTrackHeight(carouselPanel) {
		const track = carouselPanel.querySelector('.splide__track');

		if (!track) {
			return 0;
		}

		return measureWhileVisible(carouselPanel, () => track.getBoundingClientRect().height);
	}

	function setLockedPanelHeight() {
		// Height lock prevents tab-switch jump on desktop side-by-side layout only.
		if (isMobileViewport()) {
			clearLockedPanelHeight();
			return;
		}

		let maxCarouselHeight = 0;
		let maxInfoHeight = 0;

		carouselPanels.forEach((panel) => {
			const height = measureCarouselTrackHeight(panel);
			maxCarouselHeight = Math.max(maxCarouselHeight, height);
		});

		infoPanels.forEach((panel) => {
			const height = measureWhileVisible(panel, () => panel.getBoundingClientRect().height);
			maxInfoHeight = Math.max(maxInfoHeight, height);
		});

		if (maxCarouselHeight > 0) {
			section.style.setProperty(carouselHeightVar, `${Math.ceil(maxCarouselHeight)}px`);
		} else {
			section.style.removeProperty(carouselHeightVar);
		}

		if (maxInfoHeight > 0) {
			section.style.setProperty(infoHeightVar, `${Math.ceil(maxInfoHeight)}px`);
		} else {
			section.style.removeProperty(infoHeightVar);
		}
	}

	function requestLockedHeightRecompute() {
		if (resizeTimeout) {
			window.clearTimeout(resizeTimeout);
		}

		resizeTimeout = window.setTimeout(() => {
			setLockedPanelHeight();
			resizeTimeout = null;
		}, resizeDebounceMs);
	}

	anchors.forEach((anchor) => {
		const panelId = anchor.getAttribute('data-carousel-panel');

		if (panelId && !carouselPanelMap.has(panelId)) {
			console.warn(
				`carousel-sidebar-anchor: no .carousel-dynamic panel for data-carousel-panel="${panelId}"`
			);
		}

		if (panelId && infoPanels.length > 0 && !infoPanels.some((p) => p.getAttribute('data-carousel-panel') === panelId)) {
			console.warn(
				`carousel-sidebar-anchor: no .carousel-sidebar-info panel for data-carousel-panel="${panelId}"`
			);
		}
	});

	const defaultPanelId =
		section.getAttribute('data-carousel-panel-default') ||
		anchors[0].getAttribute('data-carousel-panel');

	/**
	 * @param {string} panelId
	 * @param {{ skipLazyInit?: boolean }} [options]
	 */
	function activate(panelId, options = {}) {
		if (!panelId || !carouselPanelMap.has(panelId)) {
			return;
		}

		const activeAnchor = anchors.find((a) => a.getAttribute('data-carousel-panel') === panelId);
		const panelLabel = activeAnchor ? getAnchorDisplayData(activeAnchor).label : '';

		anchors.forEach((anchor) => {
			const isActive = anchor.getAttribute('data-carousel-panel') === panelId;
			setAnchorActive(anchor, isActive);

			if (isActive) {
				syncDropdownTrigger(anchor);
			}
		});

		infoPanels.forEach((panel) => {
			setPanelVisible(panel, panel.getAttribute('data-carousel-panel') === panelId);
		});

		carouselPanels.forEach((panel) => {
			const isActive = panel.getAttribute('data-carousel-panel') === panelId;

			setPanelVisible(panel, isActive);

			if (!isActive) {
				return;
			}

			if (panel.dataset.dynamicCarouselInitialized === 'true') {
				refreshCarousel(panel);
				requestLockedHeightRecompute();
				return;
			}

			if (!options.skipLazyInit) {
				initSingleDynamicCarousel(panel);
				scheduleCarouselControlSync(panel);
				requestLockedHeightRecompute();
			}
		});

		requestLockedHeightRecompute();

		if (panelLabel) {
			liveRegion.textContent = `Panel activated: ${panelLabel}`;
		}
	}

	anchors.forEach((anchor) => {
		if (!anchor.hasAttribute('role')) {
			anchor.setAttribute('role', 'button');
		}

		const arrowSvg = anchor.querySelector('svg');
		if (arrowSvg && !arrowSvg.hasAttribute('aria-hidden')) {
			arrowSvg.setAttribute('aria-hidden', 'true');
		}

		anchor.addEventListener('click', (event) => {
			event.preventDefault();

			const panelId = anchor.getAttribute('data-carousel-panel');

			if (panelId) {
				activate(panelId);
				closeDropdown();
			}
		});
	});

	infoPanels.forEach((panel) => {
		setPanelVisible(panel, false);
	});

	carouselPanels.forEach((panel) => {
		setPanelVisible(panel, false);
	});

	if (defaultPanelId) {
		activate(defaultPanelId, { skipLazyInit: true });
	}

	// Recompute after initial page layout + after Splide init on DOMContentLoaded.
	window.requestAnimationFrame(() => {
		setLockedPanelHeight();
		window.requestAnimationFrame(() => setLockedPanelHeight());
	});

	window.addEventListener('resize', requestLockedHeightRecompute, { passive: true });
	window.addEventListener(
		'resize',
		() => {
			if (!isMobileViewport()) {
				closeDropdown();
				return;
			}

			clearLockedPanelHeight();
		},
		{ passive: true }
	);
}

export function initCarouselSidebarAnchor() {
	document.querySelectorAll('.carousel-sidebar-anchor').forEach(initCarouselSidebarAnchorSection);
}
